import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export function useDashboardStats() {
  return useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());

      // Validation Issues
      const { count: issuesCount } = await supabase
        .from("policy_flag")
        .select("*", { count: "exact", head: true })
        .in("severity", ["warn", "block"]);

      // Documents Processed This Week
      const { count: documentsProcessedCount } = await supabase
        .from("scanned_document")
        .select("*", { count: "exact", head: true })
        .eq("status", "routed")
        .gte("updated_at", weekStart.toISOString());

      return {
        validationIssues: issuesCount || 0,
        documentsProcessedThisWeek: documentsProcessedCount || 0,
      };
    },
  });
}

type ActivityItem = {
  action: string;
  detail: string;
  time: string;
  occurredAt: number;
  variant: "success" | "info" | "warning";
};

const RELEVANT_DOC_EVENTS = [
  "file_detected",
  "route_succeeded",
  "route_failed",
  "needs_review",
] as const;

export function useRecentActivity() {
  return useQuery({
    queryKey: ["recent-activity"],
    queryFn: async () => {
      const docLogs = await supabase
        .from("document_process_log")
        .select("event, occurred_at, details, document:scanned_document(original_filename)")
        .in("event", RELEVANT_DOC_EVENTS as unknown as string[])
        .order("occurred_at", { ascending: false })
        .limit(5);

      const items: ActivityItem[] = [];

      for (const row of docLogs.data ?? []) {
        const occurred = new Date(row.occurred_at);
        const filename =
          (row as any).document?.original_filename ||
          (row.details as any)?.filename ||
          "Document";
        const map: Record<string, { action: string; variant: ActivityItem["variant"] }> = {
          file_detected: { action: "New document detected", variant: "info" },
          route_succeeded: { action: "Document routed", variant: "success" },
          needs_review: { action: "Document needs review", variant: "warning" },
          route_failed: { action: "Document routing failed", variant: "warning" },
        };
        const meta = map[row.event] ?? { action: row.event, variant: "info" as const };
        items.push({
          action: meta.action,
          detail: filename,
          time: getTimeAgo(occurred),
          occurredAt: occurred.getTime(),
          variant: meta.variant,
        });
      }

      return items
        .sort((a, b) => b.occurredAt - a.occurredAt)
        .slice(0, 5)
        .map(({ occurredAt: _omit, ...rest }) => rest);
    },
  });
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
}
